// import { Link, useNavigate } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import CardContainer from './CardContainer';
import RosterContentWithData from './RosterContent';
import DemographicsContentWithData from './demographics/DemographicsContent';
import TranscriptsContentWithData from './transcripts/TranscriptsContent';
// import CteCoursesContentWithData from './cte courses/CteCoursesContent';
import MatchingContentWithData from './matching/MatchingContent';
import TestScoresContentWithData from './test scores/TestScoresContent';
import CourseCatalogsContentWithData from './course catalog/CourseCatalogsContent';
// import useUserDetails from '../hooks/useUserDetails';
// import { USER_ROLES, DATA_CATEGORY_LABELS, fileTypes, UPLOAD_SECTIONS_IDS, XQ_SUPPORT_EMAIL } from '../../constants';
import { DATA_CATEGORY_LABELS, fileTypes, UPLOAD_SECTIONS_IDS } from '../../constants';
import OpenModalIcon from '../assets/data-upload-arrow-right.svg';
import InfoIcon from '../assets/info-icon.svg';
// import CustomAlert from '../reusable/CustomAlert';
import ContentPlaceholderCard from './ContentPlaceholderCard';
import CustomButton from '../reusable/CustomButton';
import { useDataQcOutletContext } from '../hooks/useDataQcOutletContext';
import useUploadProgress from '../hooks/useUploadProgress';
import useDistrictRequiresTestScores from '../hooks/useDistrictRequiresTestScores';
import Loader from '../reusable/Loader';
import type GlobalState from '../../interfaces/GlobalStateInterface';
import Schools from '../../interfaces/SchoolsInterface';
import DataQcActionButtons from './DataQcActionButtons';
import DataQcApprovedState from './DataQcApprovedState';
import DataQcRejectedState from './DataQcRejectedState';

import './DataQcSummary.css';

const cards = [
  {
    title: 'Roster: EOAA cohort',
    categoryName: DATA_CATEGORY_LABELS[fileTypes.STUDENT_ROSTER],
    component: <RosterContentWithData />,
    path: '',
    categoryId: UPLOAD_SECTIONS_IDS[fileTypes.STUDENT_ROSTER],
    shouldRenderViewMore: false,
    infoIcon: InfoIcon,
    tooltipTitle: 'This table displays the number of students with complete data from each of the last four years.',
  },
  {
    title: 'Test scores',
    component: <TestScoresContentWithData />,
    path: '/data-qc/test-scores',
    categoryId: UPLOAD_SECTIONS_IDS[fileTypes.TEST_SCORES],
    shouldRenderViewMore: true,
    infoIcon: InfoIcon,
    categoryName: DATA_CATEGORY_LABELS[fileTypes.TEST_SCORES],
  },
  {
    title: 'Transcripts: EOAA cohort',
    categoryName: DATA_CATEGORY_LABELS[fileTypes.STUDENT_TRANSCRIPTS],
    component: <TranscriptsContentWithData />,
    path: '/data-qc/transcripts',
    categoryId: UPLOAD_SECTIONS_IDS[fileTypes.STUDENT_TRANSCRIPTS],
    shouldRenderViewMore: true,
    infoIcon: InfoIcon,
    tooltipTitle:
      'This table displays the number of student transcripts with complete data from each of the last four years.',
  },
  // {
  //   title: 'CTE courses',
  //   categoryName: DATA_CATEGORY_LABELS[fileTypes.CTE_PATHWAYS],
  //   component: <CteCoursesContentWithData />,
  //   path: '/data-qc/cte-courses',
  //   categoryId: UPLOAD_SECTIONS_IDS[fileTypes.CTE_PATHWAYS],
  //   shouldRenderViewMore: true
  // },
  {
    title: 'Course catalogs',
    categoryName: DATA_CATEGORY_LABELS[fileTypes.COURSE_CATALOG],
    component: <CourseCatalogsContentWithData />,
    path: '/data-qc/course-catalogs',
    categoryId: UPLOAD_SECTIONS_IDS[fileTypes.COURSE_CATALOG],
    shouldRenderViewMore: true,
  },
  {
    title: 'Data Matching',
    categoryName: 'Matching',
    component: <MatchingContentWithData />,
    path: '/data-qc/matching',
    categoryId: '',
    shouldRenderViewMore: false,
    infoIcon: InfoIcon,
    tooltipTitle:
      'This section cross-references the data from all uploaded files to show you how your student, course, and test data match between them. Any mismatches are highlighted here, enabling you to review and correct errors by reuploading files from the upload page.',
  },
];

const getPageState = (
  dataQcSchool: string,
  uploadProgress: GlobalState['uploadProgressSummary']['data'],
  schoolProgressByCategory: GlobalState['schoolsUploadProgressByCategory'],
  schools: Schools[]
) => {
  // SKIP UPLOADED DATA CHECKS - TO DELETE
  let skip = true;
  if (skip) return 'completed';

  if (!dataQcSchool) {
    if (uploadProgress.progress.length + uploadProgress.completed.length === 0) return 'pending';
    if (uploadProgress.pending.length + uploadProgress.progress.length === 0) return 'completed';
    return 'progress';
  }

  const schoolData = schools.find((school) => school.id === dataQcSchool);
  if (!schoolData) return 'pending';

  const schoolsStatuses = Object.values(schoolProgressByCategory);

  const uploadCompletedForSchool = schoolsStatuses.every((schoolStatus) => {
    let uploadStatus = schoolStatus[schoolData.name];
    return uploadStatus === 'completed';
  });

  if (uploadCompletedForSchool) return 'completed';

  const anySchoolProgress = schoolsStatuses.some((schoolStatus) => {
    let uploadStatus = schoolStatus[schoolData.name];

    return uploadStatus === 'progress' || uploadStatus === 'completed';
  });

  return anySchoolProgress ? 'progress' : 'pending';
};

// const getAlertBody = (pageState: string, dataQcSchool: string) => {
//   if (pageState === 'completed') return (
//     <p className="no-margin">
//       Upload and edit additional data files on&nbsp;
//       <Link className="link" to="/data-upload">the data upload page.</Link>
//       &nbsp;Questions?&nbsp;
//       <a href={`mailto:${XQ_SUPPORT_EMAIL}`} className="link-color">Contact support</a>.
//     </p>
//   );

//   if (dataQcSchool) {
//     return <p className="no-maging">You'll need to upload data for all categories to view visualizations. Once your uploads are finished processing, refresh the page and the data will appear here.</p>;
//   } else {
//     return <p className="no-maging">To view data visualizations for the entire district, please upload data from every school. If you'd like to view data for a specific school, please use the menu above.</p>;
//   }
// };

function DataQcSummary() {
  const navigate = useNavigate();

  // const { userDetails: { data: userDetails } } = useUserDetails();

  // schools data already checked at DataQcLayout
  const {
    dataQcSchool,
    uploadProgress,
    schools,
    shouldShowActionButtons,
    updateWorkflowStatusFromTransition,
    workflowStatus,
  } = useDataQcOutletContext();
  const { schoolsUploadProgressByCategory } = useUploadProgress();
  const { requiresTestScores, isPending: isSchoolsConfigPending } = useDistrictRequiresTestScores();

  const pageState = getPageState(dataQcSchool, uploadProgress, schoolsUploadProgressByCategory, schools);

  const handleViewMore = (path: string) => {
    if (path) navigate(path);
  };

  if (isSchoolsConfigPending) return <Loader />;

  const visibleCards = requiresTestScores
    ? cards
    : cards.filter((card) => card.categoryName !== DATA_CATEGORY_LABELS[fileTypes.TEST_SCORES]);

  if (pageState === 'pending')
    return (
      <div className="data-qc-main-content main-content">
        <ContentPlaceholderCard />
      </div>
    );

  // const alertType = pageState === 'completed' ? 'warning' : 'error';
  // const alertHeader = pageState === 'completed' ? 'Missing information?' : 'More data needed';
  // const alertBody = getAlertBody(pageState, dataQcSchool);

  const isApproved = workflowStatus?.current_state === 'DISTRICT_DATA_QC_COMPLETE';
  const isRejected =
    workflowStatus?.current_state === 'EXTRACTION_COMPLETE' &&
    workflowStatus?.workflow_metadata?.district_data_qc_rejection &&
    workflowStatus.workflow_metadata.district_data_qc_rejection.length > 0;

  return (
    <div className="data-qc-main-content main-content">
      {shouldShowActionButtons && updateWorkflowStatusFromTransition && !isApproved && (
        <DataQcActionButtons updateWorkflowStatusFromTransition={updateWorkflowStatusFromTransition} />
      )}

      {isApproved && workflowStatus?.workflow_metadata?.district_data_qc_approval && (
        <DataQcApprovedState approvals={workflowStatus.workflow_metadata.district_data_qc_approval} />
      )}

      {isRejected && workflowStatus?.workflow_metadata?.district_data_qc_rejection && (
        <DataQcRejectedState rejections={workflowStatus.workflow_metadata.district_data_qc_rejection} />
      )}
      {/* {userDetails && userDetails.role !== USER_ROLES.XQ_USER ?
        <CustomAlert alertType={alertType} header={alertHeader}>
          <div className="data-qc-info-container">
            {alertBody}
          </div>
        </CustomAlert>
        :
        null
      } */}
      <CardContainer
        title="Demographics"
        categoryDataLabel={DATA_CATEGORY_LABELS[fileTypes.STUDENT_DEMOGRAPHICS]}
        categoryId={UPLOAD_SECTIONS_IDS[fileTypes.STUDENT_DEMOGRAPHICS]}
      >
        <DemographicsContentWithData />
      </CardContainer>
      <div className="data-qc-cards-container">
        {visibleCards.map(
          (
            { title, component, path, shouldRenderViewMore, infoIcon, tooltipTitle, categoryName, categoryId },
            index
          ) => (
            <CardContainer
              key={index}
              title={title}
              tooltipTitle={tooltipTitle}
              button={
                shouldRenderViewMore ? (
                  <CustomButton
                    variant="text"
                    buttonText="View more"
                    buttonAction={() => handleViewMore(path)}
                    icon={<img src={OpenModalIcon} alt="open data qc summary details view"></img>}
                    iconPosition="end"
                    sx={{ '& .MuiButton-endIcon': { marginLeft: 'var(--scaling-spacing-4)' } }}
                  />
                ) : null
              }
              classes={['data-qc-summary-card']}
              icon={infoIcon}
              categoryDataLabel={categoryName}
              categoryId={categoryId}
            >
              {component}
            </CardContainer>
          )
        )}
      </div>
    </div>
  );
}

export default DataQcSummary;
